import { Spacing } from '@persona-id/design-tokens';
import { zipObject } from 'lodash';
import { partition, snakeCase, startCase } from 'lodash/fp';
import { Fragment, ReactNode } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import {
  ADDRESS_SUBFIELDS_DISPLAY_NAMES,
  NAME_SUBFIELDS_DISPLAY_NAMES,
} from 'dashboard/components/ExtractionPanel/ExtractionFieldWriteBacksPanel';
import {
  ExtractionPanelForm,
  FieldWriteBackPanelForm,
} from 'dashboard/components/ExtractionPanel/util';
import {
  ExtractionRequest,
  ExtractionType,
  FieldWriteBackRequest,
  FieldWriteBackSourceType,
  GuidedPreset,
} from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import { ErrorText, WarningText } from 'design-system/components/FormControl';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleGroup, { ToggleGroupItem } from 'design-system/components/ToggleGroup';
import Tooltip from 'design-system/components/Tooltip';
import { FIELD_SCHEMA_TYPE_DISPLAY, FieldSchema, FieldSchemaType } from 'lib/fields';

const COMPATIBLE_DOCUMENT_FIELD_TYPES = [FieldSchemaType.String, FieldSchemaType.Array];

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const SelectWithDescriptions = styled(Select)`
  ${Paragraph} {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  color: ${({ theme }) => theme.color.variants.primary.default};
  text-decoration: underline;
  &:focus:not(:disabled) {
    box-shadow: none;
  }
`;

// TODO (Documents): Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) =>
    !disabled &&
    css`
      text-decoration: underline;
      text-decoration-style: dotted;
    `}
`;

const TooltipParagraph = styled(Paragraph)`
  width: 200px;
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface ExtractionPanelAdvancedProps {
  displayName?: string;
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  vtmplVersion: VerificationTemplateVersionDocument;
  fieldWriteBackFormIndex: number;
  rightButton?: ReactNode;
  showClearFieldButton?: boolean;
  disabled?: boolean;
}

const errorRequired = { type: 'required', message: 'Required' };

export const singleFieldWriteBackRequestToFieldWriteBackFormState = (
  extractionRequest: ExtractionRequest,
  fieldWriteBack: FieldWriteBackRequest,
  fieldSchema: FieldSchema[],
): FieldWriteBackPanelForm[] => {
  const fieldSchemaType = fieldSchema.find(
    (field) => field.key === fieldWriteBack['field-name'],
  )?.type;
  let formFieldSchemaType: FieldSchemaType.Array | FieldSchemaType.String;
  switch (fieldSchemaType) {
    case FieldSchemaType.Array:
    case FieldSchemaType.String:
      formFieldSchemaType = fieldSchemaType;
      break;
    // this shouldn't happen but for now default to string
    default:
      formFieldSchemaType = FieldSchemaType.String;
  }

  const options = fieldWriteBack.options.filter(
    (o) =>
      o['source-type'] === FieldWriteBackSourceType.Extraction &&
      o['source-key'] === extractionRequest['field-name'],
  );

  if (options.length === 0) {
    return [];
  }

  const fieldWritebackForms: FieldWriteBackPanelForm[] = options.map((option) => ({
    isNewDocumentField: false,
    subfieldName: option.selector?.['specified-field-identifiers']?.[0],
    documentFieldKey: fieldWriteBack['field-name'],
    documentFieldName: startCase(fieldWriteBack['field-name']),
    documentFieldType: formFieldSchemaType,
  }));

  if (
    extractionRequest['extraction-type'] === ExtractionType.Guided &&
    extractionRequest.version === '3.0'
  ) {
    switch (extractionRequest.preset) {
      case GuidedPreset.AddressStrict:
      case GuidedPreset.AddressLoose: {
        // assume field writebacks are stored in order
        const subfields = extractionRequest.subfields.map((sf) => sf['source-field']);
        const subfieldNameToDisplayName = zipObject(subfields, ADDRESS_SUBFIELDS_DISPLAY_NAMES);
        // only overwrite display name for fieldwriteback for subfields,
        // not fieldwritebacks for the entire extraction
        fieldWritebackForms.forEach(
          // subfieldName should never be undefined because we filter above
          // but adding this to satisfy tsc checks
          (fw) => (fw.displayName = subfieldNameToDisplayName[fw.subfieldName ?? '']),
        );
        break;
      }
      case GuidedPreset.NameLoose:
      case GuidedPreset.NameStrict: {
        // assume field writebacks are stored in order
        const subfields = extractionRequest.subfields.map((sf) => sf['source-field']);
        const subfieldNameToDisplayName = zipObject(subfields, NAME_SUBFIELDS_DISPLAY_NAMES);
        // only overwrite display name for fieldwriteback for subfields,
        // not fieldwritebacks for the entire extraction
        fieldWritebackForms.forEach(
          // subfieldName should never be undefined because we filter above
          // but adding this to satisfy tsc checks
          (fw) => (fw.displayName = subfieldNameToDisplayName[fw.subfieldName ?? '']),
        );
        break;
      }
    }
  }
  return fieldWritebackForms;
};

export const validateSingleFieldWriteBackPanelForm = (
  data: FieldWriteBackPanelForm,
  documentFields: FieldSchema[],
) => {
  const errors: {
    [K in keyof FieldWriteBackPanelForm]?: { type: string; message: string };
  } = {};
  const usedDocumentFieldNames = documentFields.map((documentField) => documentField.label);
  const usedDocumentFieldKeys = documentFields.map((documentField) => documentField.key);

  if (data?.isNewDocumentField) {
    if (data?.documentFieldName == null || data?.documentFieldName === '') {
      errors.documentFieldName = errorRequired;
    } else if (usedDocumentFieldNames.includes(data.documentFieldName)) {
      errors.documentFieldName = {
        type: 'conflict',
        message:
          'A document field with this label already exists. Please choose a different label.',
      };
    }
    if (usedDocumentFieldKeys.includes(data.documentFieldKey)) {
      errors.documentFieldKey = {
        type: 'conflict',
        message: 'A document field with this key already exists. Please choose a different key.',
      };
    }
    if (data?.documentFieldType == null) {
      errors.documentFieldType = errorRequired;
    }
  } /* not a new field */ else if (
    data?.documentFieldKey == null ||
    data?.documentFieldKey === ''
  ) {
    errors.documentFieldKey = errorRequired;
  }
  return errors;
};

const ExtractionSingleFieldWritebackPanel = ({
  formReturn,
  vtmplVersion,
  fieldWriteBackFormIndex,
  displayName,
  rightButton,
  showClearFieldButton,
  disabled = false,
}: ExtractionPanelAdvancedProps) => {
  const { register, formState, clearErrors, setValue, getValues, control } = formReturn;
  const { errors } = formState;

  const extractionName = getValues('fieldName');
  const fieldWriteBackForms = getValues('fieldWriteBackForms');
  const fieldWriteBackForm = fieldWriteBackForms[fieldWriteBackFormIndex];
  const isNewDocumentField = fieldWriteBackForm?.isNewDocumentField;
  const documentFieldKey = fieldWriteBackForm?.documentFieldKey;

  const documentFields = vtmplVersion.attributes['field-schemas'] || [];
  const [compatibleDocumentFields, incompatibleDocumentFields] = partition(
    (documentField) => COMPATIBLE_DOCUMENT_FIELD_TYPES.includes(documentField.type),
    documentFields,
  );

  const fieldWriteBacks = vtmplVersion.attributes['field-write-backs'] || [];

  const sourcesWritingToField =
    fieldWriteBacks
      .find((fieldWriteBack) => fieldWriteBack['field-name'] === documentFieldKey)
      // filter out the current source to not conflict with itself
      ?.options.filter(
        (option) => option['source-key'] !== snakeCase(formState.defaultValues?.fieldName || ''),
      )
      .map((option) => ({
        source_key: option['source-key'],
        source_type: option['source-type'],
      })) || [];

  const clearField = () => {
    setValue(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldName`, '');
    setValue(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`, '');
  };

  return (
    <>
      {displayName && (
        <Paragraph
          size='lg'
          weight='bold'
          color='primary'
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'top' }}
        >
          {displayName}
          {rightButton && rightButton}
        </Paragraph>
      )}
      <Spacer height={1.5} />
      <Controller
        name={`fieldWriteBackForms.${fieldWriteBackFormIndex}.isNewDocumentField`}
        control={control}
        render={({ field }) => (
          <ToggleGroup
            value={field.value?.toString() || 'false'}
            onValueChange={(e) => {
              field.onChange(e === 'true');

              const fieldName = getValues('fieldName');
              const fieldKey = snakeCase(fieldName);
              const compatibleExists = compatibleDocumentFields.some(({ key }) => key === fieldKey);
              if (field.value) {
                setValue(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldName`, '');
                setValue(
                  `fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`,
                  compatibleExists ? fieldKey : '',
                );
              } else {
                setValue(
                  `fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldName`,
                  compatibleExists ? '' : fieldName,
                );
                setValue(
                  `fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`,
                  compatibleExists ? '' : fieldKey,
                );
              }
              setValue(
                `fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldType`,
                FieldSchemaType.String,
              );
              clearErrors(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldName`);
              clearErrors(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`);
              clearErrors(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldType`);
            }}
            type='single'
            fullWidth
            disabled={disabled}
          >
            <ToggleGroupItem value='false'>Existing field</ToggleGroupItem>
            <ToggleGroupItem value='true'>New field</ToggleGroupItem>
          </ToggleGroup>
        )}
      />
      {isNewDocumentField ? (
        <>
          <Spacer height={1.5} />
          <StyledLabel>New document field label</StyledLabel>
          <Controller
            name={`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldName`}
            control={control}
            render={({ field }) => (
              <FieldWrapper>
                <Input
                  {...field}
                  placeholder='Enter new document field label'
                  hasError={
                    !!errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldName
                  }
                  onChange={(e) => {
                    field.onChange(e);
                    setValue(
                      `fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`,
                      snakeCase(e.target.value),
                    );
                  }}
                  disabled={disabled}
                />
              </FieldWrapper>
            )}
          />
          <FieldErrorText
            message={
              errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldName?.message
            }
          />
          <Spacer height={1.5} />
          <StyledLabel>Document field key</StyledLabel>
          <FieldWrapper>
            <Input
              {...register(`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`)}
              disabled
              hasError={!!errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldKey}
            />
          </FieldWrapper>
          <FieldErrorText
            message={
              errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldKey?.message
            }
          />
          <Spacer height={1.5} />
          <Tooltip
            side='right'
            trigger={
              <StyledLabel>
                <TooltipIndicatorWrapper disabled={disabled}>Type</TooltipIndicatorWrapper>
              </StyledLabel>
            }
            disabled={disabled}
          >
            <TooltipParagraph>
              Extractions can be saved to String or Array of String document fields. Incompatible
              fields are disabled.
            </TooltipParagraph>
          </Tooltip>
          <Controller
            name={`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldType`}
            control={control}
            render={({ field }) => (
              <FieldWrapper>
                <Select
                  {...field}
                  placeholder='Select type'
                  hasError={
                    !!errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldType
                  }
                  disabled={disabled}
                >
                  {COMPATIBLE_DOCUMENT_FIELD_TYPES.map((fieldType) => (
                    <Option key={fieldType} value={fieldType}>
                      {FIELD_SCHEMA_TYPE_DISPLAY[fieldType]}
                    </Option>
                  ))}
                </Select>
              </FieldWrapper>
            )}
          />
          <FieldErrorText message={errors?.extractorType?.message} />
        </>
      ) : (
        <>
          <Spacer height={1.5} />
          <Tooltip
            side='right'
            trigger={
              <StyledLabel>
                <TooltipIndicatorWrapper disabled={disabled}>
                  Document fields
                </TooltipIndicatorWrapper>
              </StyledLabel>
            }
            disabled={disabled}
          >
            <TooltipParagraph>
              Extractions can be saved to String or Array of String document fields. Incompatible
              fields are disabled.
            </TooltipParagraph>
          </Tooltip>
          <Controller
            name={`fieldWriteBackForms.${fieldWriteBackFormIndex}.documentFieldKey`}
            control={control}
            render={({ field }) => (
              <FieldWrapper>
                <SelectWithDescriptions
                  {...field}
                  placeholder={
                    documentFields.length > 0
                      ? 'Select document field'
                      : 'No existing document fields'
                  }
                  hasError={
                    !!errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldKey
                  }
                  hasWarning={sourcesWritingToField.length > 0}
                  disabled={disabled || documentFields.length === 0}
                >
                  {compatibleDocumentFields.map((documentField) => (
                    <Option key={documentField.key} value={documentField.key}>
                      {documentField.label}
                      <Paragraph>
                        {FIELD_SCHEMA_TYPE_DISPLAY[documentField.type]}: {documentField.key}
                      </Paragraph>
                    </Option>
                  ))}
                  {incompatibleDocumentFields.map((documentField) => (
                    <Option key={documentField.key} value={documentField.key} disabled>
                      {documentField.label}
                      <Paragraph>
                        {FIELD_SCHEMA_TYPE_DISPLAY[documentField.type]}: {documentField.key}
                      </Paragraph>
                    </Option>
                  ))}
                </SelectWithDescriptions>
              </FieldWrapper>
            )}
          />
          <FieldErrorText
            message={
              errors?.fieldWriteBackForms?.[fieldWriteBackFormIndex]?.documentFieldKey?.message
            }
          />
          {sourcesWritingToField.map((source) => (
            <Fragment key={source.source_key}>
              <Spacer height={1.5} />
              <WarningText>
                This field is already being written to by {source.source_type} {source.source_key}
              </WarningText>
            </Fragment>
          ))}
        </>
      )}

      {showClearFieldButton && (
        <>
          <Spacer height={1.5} />
          <StyledButton
            id={`${extractionName}.fieldWriteBackForms.${fieldWriteBackFormIndex}`}
            onClick={clearField}
            variant='inverse'
            width='auto'
          >
            Clear field
          </StyledButton>
          <Spacer height={1.5} />
        </>
      )}
    </>
  );
};

export default ExtractionSingleFieldWritebackPanel;
